import {combineReducers} from '@reduxjs/toolkit';
import ambientWeatherReducer from './ambientWeather';
import billingReducer from './billing';
import dataReducer from './data';
import davisReducer from './davis';
import ecowittReducer from './ecowitt';
import featureFlagsReducer from './featureFlags';
import locationReducer from './location';
import myAcuriteReducer from './myAcurite';
import netatmoEnergyReducer from './netatmoEnergy';
import netatmoWeatherReducer from './netatmoWeather';
import staffElevationReducer from './staffElevation';
import uiInteractionReducer from './uiInteraction';
import userReducer from './user';
import userPreferenceReducer from './userPreference';
import weatherReducer from './weather';
import weatherDataReducer from './weatherData';
import weatherflowReducer from './weatherflow';
import wundergroundReducer from './wunderground';

/**
 * The slices every app that consumes core's selectors must register.
 *
 * A plain object, deliberately, not a `configureStore` call. This module used
 * to build a throwaway store purely so the type below could be inferred from
 * it — which was free only for as long as nothing imported a *value* from
 * here: every existing importer takes `CommonRootState` alone, a type, which
 * the compiler elides, so the module was never actually loaded at runtime. The
 * moment `COMMON_SLICE_NAMES` gave it a value export that stopped being true,
 * and a second store would have been constructed on every app start — and, in
 * a dev build, registered itself with the Redux DevTools extension alongside
 * the real one. `combineReducers` gives the same inference with no instance.
 */
const commonReducers = combineReducers({
  user: userReducer,
  userPreference: userPreferenceReducer,
  uiInteraction: uiInteractionReducer,
  billing: billingReducer,
  featureFlags: featureFlagsReducer,
  data: dataReducer,
  weather: weatherReducer,
  weatherData: weatherDataReducer,
  ambientWeather: ambientWeatherReducer,
  davis: davisReducer,
  ecowitt: ecowittReducer,
  myAcurite: myAcuriteReducer,
  netatmoEnergy: netatmoEnergyReducer,
  netatmoWeather: netatmoWeatherReducer,
  weatherflow: weatherflowReducer,
  wunderground: wundergroundReducer,
  location: locationReducer,
  staffElevation: staffElevationReducer,
});

// Inferred from the reducer map above, so the two cannot drift.
export type CommonRootState = ReturnType<typeof commonReducers>;

/**
 * The slices every consuming app must register, at runtime.
 *
 * `CommonRootState` is a *type*, so an app whose reducer map is missing a slice
 * type-checks and then throws the first time a shared selector reads it. That
 * is exactly what happened to the webapp: it registered 18 reducers, none of
 * them `location`, and `selectCoverageStations` — which reads both forecast
 * stores by design — took `/app/settings/preferences` to the error boundary
 * with "Cannot read properties of undefined (reading 'locationList')"
 * (SMARTMIXIN-WEB-5C).
 *
 * Derived from the same reducer map as the type above, so the two cannot
 * disagree. Both apps assert against it in their own store test.
 */
export const COMMON_SLICE_NAMES = Object.keys(
  // `combineReducers` answers an unknown action with the initial state, which
  // is the cheapest way to enumerate the keys it was built from.
  commonReducers(undefined, {type: '@@core/PROBE_SLICE_NAMES'}),
) as (keyof CommonRootState)[];
